<script setup lang="ts">
import { useAccountStore } from '@/stores/accountStore'
import { useRoute, useRouter } from 'vue-router'
import { ref, onMounted, watch, computed } from 'vue'
import {
  fetchAccountSingle,
  fetchAccountsProduct,
  toggleProductForAccount,
  VerifyAdmin,
  fetchProducts,
  toggleAccountStatus,
  getParentAccount,
  getParentChild,
  fetchAccountProductsAdmin,
  fetchAllProductsFromIndex,
  deleteProductFromAccount,
} from '@/api/accountsAdmin'
import { fetchUsageMonthly, fetchUsageRange } from '@/api/accountServiceUsage'
import { useUserStore } from '@/stores/userStore'
import Cookies from 'universal-cookie'
import { ACCOUNT_SERVICE_USAGE_API_URL } from '@/env'
import {
  buildProductCostUsdMap,
  buildProductModuleCostUsdMap,
  formatModuleLabel,
  formatUsd,
  formatUsdPrecise,
} from '@/utils/usageCosts'

const dash = (v: any) => (v === null || v === undefined || v === '' ? '—' : String(v))
const unavailableCostLabel = '--'

const asLink = (url?: string): string | undefined => {
  if (!url) return undefined
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`)
    return u.href.replace(/\/+$/, '')
  } catch {
    return undefined
  }
}

const telHref = (phone?: string): string | undefined => {
  if (!phone) return undefined
  return `tel:${phone.replace(/[^\d+]/g, '')}`
}

const copyToClipboard = async (text?: string) => {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
  } catch (e) {
    console.warn('Copy failed', e)
  }
}

const { role } = useUserStore()
const store = useAccountStore()
const route = useRoute()
const accountName: string = String(route.params.name || '')
const accountData = ref<any>(null)
const isLoading = ref(true)
const accountProducts = ref<any[]>([])
const selectedAccount = ref<{ accountName: string; productName: string; enabled: boolean } | null>(
  null
)
const dialog = ref(false)
const loadingProd = ref(false)
const productsLoaded = ref(false)
const parentList = ref<any[]>([])
const expandedProducts = ref<Set<string>>(new Set())
const childrenCache = ref<Record<string, any[]>>({})
const childrenLoading = ref<Record<string, boolean>>({})

const usageCostLoading = ref(false)
const usageCostByProduct = ref<Record<string, number>>({})
const usageCostByProductModule = ref<Record<string, Record<string, number>>>({})
const usageCostStartMonth = ref<string>('')
const usageCostEndMonth = ref<string>('')
const usageCostAccountId = ref<string>('')
const usageCostError = ref<string>('')
const costStartMonth = ref<string>(currentMonthIso())
const costMonthMenu = ref(false)
const costPickerMode = ref<'months' | 'year'>('months')
const costPickerYear = ref<number>(Number(currentMonthIso().slice(0, 4)) || new Date().getFullYear())
// Vuetify date-picker `month` prop is 0-based (0=Jan)
const costPickerMonth0 = ref<number>(new Date().getMonth())
const suppressMonthPick = ref(false)

function currentMonthIso(): string {
  return new Date().toISOString().slice(0, 7)
}

function isValidMonthIso(v: string): boolean {
  if (!/^\d{4}-\d{2}$/.test(v)) return false
  const m = Number(v.slice(5, 7))
  return Number.isInteger(m) && m >= 1 && m <= 12
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

const costEndMonth = computed(() => currentMonthIso())

function normalizeMonthNumber(m: unknown): number | null {
  const n = Number(m)
  if (!Number.isFinite(n)) return null
  // Normalize to 0-11. Vuetify can emit 0-11 or 1-12 depending on internals.
  if (n >= 0 && n <= 11) return n
  if (n >= 1 && n <= 12) return n - 1
  return null
}

function parseMonthIso(v: string): { year: number; month: number } | null {
  const s = String(v || '').trim()
  if (!isValidMonthIso(s)) return null
  const y = Number(s.slice(0, 4))
  const m = Number(s.slice(5, 7))
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null
  return { year: y, month: m }
}

const costStartMonthDate = computed<string>({
  get: () => {
    const m = String(costStartMonth.value || '').trim()
    const ym = isValidMonthIso(m) ? m : costEndMonth.value
    return `${ym}-01`
  },
  set: (picked: any) => {
    let ym = ''
    if (picked instanceof Date && !Number.isNaN(picked.getTime())) {
      ym = `${picked.getFullYear()}-${pad2(picked.getMonth() + 1)}`
    } else {
      const s = String(picked || '')
      if (/^\d{4}-\d{2}/.test(s)) ym = s.slice(0, 7)
    }
    if (isValidMonthIso(ym)) costStartMonth.value = ym
    costMonthMenu.value = false
  },
})

function onCostPickerViewMode(v: unknown) {
  const s = String(v || '')
  if (s === 'months' || s === 'year') costPickerMode.value = s as any
}

function onCostPickerYear(y: unknown) {
  const yr = Number(y)
  if (Number.isFinite(yr)) costPickerYear.value = yr
  // When user picks a year in "year" view, switch back to months.
  costPickerMode.value = 'months'
}

function applyCostStartMonthFromMonth0(month0: number) {
  const m1 = month0 + 1
  const ym = `${costPickerYear.value}-${pad2(m1)}`
  if (!isValidMonthIso(ym)) return
  costStartMonth.value = ym
  costMonthMenu.value = false
}

function onCostPickerMonthUpdate(m: unknown) {
  const month0 = normalizeMonthNumber(m)
  if (month0 == null) return
  costPickerMonth0.value = month0

  // In months view, treat month change as a selection and close/apply.
  if (costPickerMode.value === 'months' && costMonthMenu.value) {
    if (suppressMonthPick.value) return
    applyCostStartMonthFromMonth0(month0)
  }
}

function onCostPickerMonthSlotClick(slotProps: any, monthValue: unknown) {
  try {
    slotProps?.onClick?.()
  } catch {}
  const month0 = normalizeMonthNumber(monthValue)
  if (month0 == null) return
  applyCostStartMonthFromMonth0(month0)
}

function normKey(v: unknown): string {
  return String(v ?? '').trim().toLowerCase()
}

function getMonthlyModuleCostsForRow(item: any): Array<{ module: string; costUsd: number }> {
  if (!item || item?.synthetic || item?.isLoadingRow) return []
  // Keep table readable: show costs only on top-level products
  if (Number(item?.level || 0) > 0) return []

  const m = usageCostByProductModule.value || {}

  const candidates = [
    item?.simpleProductName,
    item?.productName,
    item?.productId,
    item?.path,
    String(item?.path || item?.productId || '').split('/')[0] || '',
  ]
    .map((x) => normKey(x))
    .filter(Boolean)

  for (const k of candidates) {
    const byMod = m[k]
    if (!byMod) continue
    return Object.entries(byMod)
      .map(([module, cost]) => ({ module, costUsd: Number(cost || 0) }))
      .filter((r) => Number.isFinite(r.costUsd) && r.costUsd > 0)
      .sort((a, b) => b.costUsd - a.costUsd)
  }

  return []
}

function getMonthlyCostUsdForRow(item: any): number | null {
  if (!item || item?.synthetic || item?.isLoadingRow) return null
  // Keep table readable: show costs only on top-level products
  if (Number(item?.level || 0) > 0) return null

  const m = usageCostByProduct.value || {}

  const candidates = [
    item?.simpleProductName,
    item?.productName,
    item?.productId,
    item?.path,
    String(item?.path || item?.productId || '').split('/')[0] || '',
  ]
    .map((x) => normKey(x))
    .filter(Boolean)

  for (const k of candidates) {
    if (k in m) {
      const v = Number(m[k] || 0)
      return v > 0 ? v : null
    }
  }

  return null
}

async function loadCurrentMonthUsageCosts() {
  usageCostError.value = ''
  if (!canSeeMonthlyCost.value) return
  const base = String(ACCOUNT_SERVICE_USAGE_API_URL || '').trim()
  const accountId = String(accountData.value?.juid || '').trim()
  if (!base || !accountId) return

  const startMonth = String(costStartMonth.value || '').trim() || costEndMonth.value
  const endMonth = costEndMonth.value
  if (!isValidMonthIso(startMonth)) return

  if (
    usageCostStartMonth.value === startMonth &&
    usageCostEndMonth.value === endMonth &&
    usageCostAccountId.value === accountId
  )
    return

  usageCostLoading.value = true
  try {
    try {
      const out = await fetchUsageRange({ accountId, startMonth, endMonth })
      usageCostByProduct.value = buildProductCostUsdMap(out.items || [])
      usageCostByProductModule.value = buildProductModuleCostUsdMap(out.items || [])
    } catch {
      // Backward-compatible fallback if /usage/range isn't deployed yet
      const out = await fetchUsageMonthly({ accountId, month: endMonth })
      usageCostByProduct.value = buildProductCostUsdMap(out.items || [])
      usageCostByProductModule.value = buildProductModuleCostUsdMap(out.items || [])
    }

    usageCostStartMonth.value = startMonth
    usageCostEndMonth.value = endMonth
    usageCostAccountId.value = accountId
  } catch (e: any) {
    usageCostByProduct.value = {}
    usageCostByProductModule.value = {}
    usageCostStartMonth.value = startMonth
    usageCostEndMonth.value = endMonth
    usageCostAccountId.value = accountId
    usageCostError.value = String(e?.message || e || 'Failed to load costs')
  } finally {
    usageCostLoading.value = false
  }
}

const accountNameSend = ref('')
const productNameSend = ref('')
const newStatus = ref(false)
const user = useUserStore()

const cookie = new Cookies()
const admin = cookie.get('admin')
const userRole = ref(user?.role)

// Show product ID column for super admin and owner roles
const canSeeProductId = computed(() => {
  return ['company_suadmin', 'company_owner'].includes(String(userRole.value))
})

// Only show/fetch monthly costs for these roles
const canSeeMonthlyCost = computed(() => {
  return ['company_suadmin', 'company_owner'].includes(String(userRole.value))
})

const router = useRouter()
function goCreateUser(item: any) {
  // Use new simple user creation flow
  router.push({
    path: '/users/create-client-user-simple',
    query: {
      juid: item?.juid,
      accountName: item?.name,
    },
  })
}

// Section visibility driven by hash
const showInfoSection = computed(() => route.hash !== '#products')
const showProductsSection = computed(() => route.hash === '#products')

const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref<'success' | 'error' | 'info' | 'warning'>('success')
const showSnack = (text: string, color: 'success' | 'error' | 'info' | 'warning' = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

// Remove product confirmation dialog
const removeDialog = ref(false)
const productToRemove = ref<{ productName: string; productFriendlyName: string } | null>(null)
const removingProduct = ref(false)

const toggleAccountFunc = async () => {
  try {
    if (!selectedAccount.value) return
    const toggleData = selectedAccount.value
    const acct = String(toggleData.accountName)
    const productName = String(toggleData.productName)
    const enabling = Boolean(toggleData.enabled)

    // Backend now handles recursive child disabling automatically
    await toggleProductForAccount(acct, productName)

    showSnack(`Product ${enabling ? 'enabled' : 'disabled'} successfully`, 'success')
    await loadAccountProducts()
  } catch (error) {
    console.error('Error toggling product:', error)
    showSnack('Failed to toggle product', 'error')
    await loadAccountProducts()
  } finally {
    dialog.value = false
  }
}

const confirmToggleAccountStatus = async (productName: string, isActive: boolean) => {
  selectedAccount.value = {
    accountName,
    productName,
    enabled: !isActive,
    children: [], // Will be populated if disabling
  }

  // If disabling, fetch children to show in confirmation
  if (isActive) {
    try {
      const children = await getParentChild(productName)
      if (Array.isArray(children) && children.length > 0) {
        selectedAccount.value.children = children
          .map((c: any) => c?.productFriendlyName || c?.productName || c?.path || '')
          .filter(Boolean)
      }
    } catch (e) {
      console.log('Could not fetch children for confirmation dialog')
    }
  }

  dialog.value = true
}

const removeProductFromAccount = async () => {
  if (!productToRemove.value) return

  removingProduct.value = true
  try {
    await deleteProductFromAccount(accountName, productToRemove.value.productName)
    showSnack(
      `Product "${productToRemove.value.productFriendlyName}" removed successfully`,
      'success'
    )
    removeDialog.value = false
    productToRemove.value = null
    await loadAccountProducts()
  } catch (error: any) {
    console.error('Failed to remove product:', error)
    showSnack(error?.response?.data?.message || 'Failed to remove product', 'error')
  } finally {
    removingProduct.value = false
  }
}

const loadAccounts = async () => {
  isLoading.value = true
  try {
    const data = await fetchAccountSingle(accountName)
    accountData.value = data
    store.selectedAccountData = data
  } catch (error) {
    console.error('Failed to load account:', error)
  } finally {
    isLoading.value = false
  }
}
const goToEdit = (productId: string) => {
  // URL-encode the productId to handle slashes in child product paths (e.g., "ise/import_homenet")
  const encodedProductId = encodeURIComponent(productId)
  console.log('encodedProductId', encodedProductId)
  router.push(`/account/edit/${String(accountName)}/${encodedProductId}`)
}

const loadAccountProducts = async () => {
  loadingProd.value = true
  try {
    const [accountProductsData, hierarchyData] = await Promise.all([
      fetchAccountsProduct(String(accountName)),
      fetchAllProductsFromIndex(),
    ])
    const hierarchyMap = new Map()
    hierarchyData.products.forEach((p: any) => {
      if (p.productId) hierarchyMap.set(p.productId, p)
      if (p.path && p.path !== p.productId) hierarchyMap.set(p.path, p)
      const simpleName = (p.productId || p.path || '').split('/').pop()
      if (simpleName && simpleName !== p.productId && simpleName !== p.path) {
        if (!hierarchyMap.has(simpleName)) hierarchyMap.set(simpleName, p)
      }
    })
    const edgesMap = new Map()
    Object.entries(hierarchyData.edges || {}).forEach(([parentId, children]: [string, any]) => {
      console.log(`Building edgesMap: parent=${parentId}, children=`, children)
      edgesMap.set(parentId, children)
      const simpleName = parentId.split('/').pop()
      if (simpleName && simpleName !== parentId && !edgesMap.has(simpleName)) {
        edgesMap.set(simpleName, children)
      }
    })
    console.log('Final edgesMap:', Object.fromEntries(edgesMap))
    accountProducts.value = accountProductsData.map((accountProduct: any) => {
      const productId = accountProduct.productName || accountProduct.productId
      const hierarchyInfo = hierarchyMap.get(productId)

      console.log(`Mapping product ${productId}, isActive:`, accountProduct.isActive)

      if (hierarchyInfo) {
        const childrenArray =
          edgesMap.get(hierarchyInfo.productId) || edgesMap.get(hierarchyInfo.path) || []

        // Check if any children from the template are actually enrolled on this account
        const enrolledProductIds = new Set(
          accountProductsData.map((p: any) => p.productName || p.productId)
        )

        console.log(`Product ${productId} children in template:`, childrenArray)
        console.log(`Enrolled products on account:`, Array.from(enrolledProductIds))

        const hasChildren = childrenArray.some((child: any) => {
          // Children can be either strings or objects depending on the data structure
          const childId = typeof child === 'string' ? child : child.productId || child.path
          // Also check simple name (last segment) for backward compatibility
          const childSimpleName = childId ? String(childId).split('/').pop() : null
          const match =
            enrolledProductIds.has(childId) ||
            (childSimpleName && enrolledProductIds.has(childSimpleName))
          if (match) {
            console.log(`✓ Found enrolled child: ${childId}`)
          }
          return match
        })

        console.log(
          `Product ${productId} has children in template:`,
          childrenArray.length,
          'enrolled children:',
          hasChildren
        )

        // Extract simple product name (last segment of path or productId)
        const simpleProductName =
          (hierarchyInfo.productId || hierarchyInfo.path || productId).split('/').pop() || productId
        const productType = hierarchyInfo.type || 'parent'
        return {
          ...accountProduct,
          path: hierarchyInfo.path || hierarchyInfo.productId || productId,
          productId: hierarchyInfo.productId || productId,
          simpleProductName: simpleProductName, // Simple flat product ID for API calls
          productFriendlyName:
            hierarchyInfo.productFriendlyName || accountProduct.productFriendlyName,
          type: productType,
          isChild: productType === 'child' || productType === 'intermediate',
          isActive: accountProduct.isActive, // Explicitly preserve isActive
          level: hierarchyInfo.level || 0,
          parentPath: hierarchyInfo.parentPath,
          hasChildren: hasChildren,
          enFlag: accountProduct.isActive,
        }
      } else {
        const hasChildrenInAccount = accountProductsData.some((p: any) => {
          const pName = p.productName || p.productId
          return pName && pName.startsWith(productId + '/')
        })
        console.log(
          `Product ${productId} (no hierarchy info) hasChildrenInAccount:`,
          hasChildrenInAccount
        )
        // Extract simple product name (last segment)
        const simpleProductName = productId.split('/').pop() || productId
        return {
          ...accountProduct,
          path: productId,
          productId: productId,
          simpleProductName: simpleProductName, // Simple flat product ID for API calls
          productFriendlyName: accountProduct.productFriendlyName || productId,
          type: 'parent',
          isChild: false,
          isActive: accountProduct.isActive, // Explicitly preserve isActive
          level: 0,
          hasChildren: hasChildrenInAccount,
          enFlag: accountProduct.isActive,
        }
      }
    })
    loadingProd.value = false
  } catch (error) {
    console.error('Failed to load account products:', error)
    loadingProd.value = false
  }
}
const headers = computed(() => {
  const cols: any[] = [
    { title: 'Product Friendly Name', value: 'productFriendlyName', sortable: true },
    { title: 'Status', value: 'status', sortable: false, align: 'start' },
    { title: 'Type', value: 'type', sortable: false, align: 'start' },
    { title: 'Actions', value: 'actions', sortable: false, align: 'end', width: '200px' },
  ]
  if (canSeeMonthlyCost.value) {
    cols.splice(3, 0, {
      title: 'Cost',
      value: 'currentMonthCost',
      sortable: false,
      align: 'end',
      width: '160px',
    })
  }
  return cols
})

onMounted(() => {
  VerifyAdmin()
  loadAccounts()
  if (route.hash === '#products' && !productsLoaded.value) {
    productsLoaded.value = true
    loadAccountProducts()
      .then(() => (canSeeMonthlyCost.value ? loadCurrentMonthUsageCosts() : undefined))
  }
})

watch(
  () => [route.hash, accountData.value?.juid, userRole.value],
  ([h, juid]) => {
    if (h !== '#products') return
    if (!juid) return
    if (!canSeeMonthlyCost.value) return
    loadCurrentMonthUsageCosts()
  }
)

watch(costStartMonth, () => {
  if (route.hash !== '#products') return
  if (!canSeeMonthlyCost.value) return
  loadCurrentMonthUsageCosts()
})

watch(costMonthMenu, (open) => {
  if (!open) return
  costPickerMode.value = 'months'
  suppressMonthPick.value = true
  const parsed = parseMonthIso(costStartMonth.value)
  if (parsed) {
    costPickerYear.value = parsed.year
    costPickerMonth0.value = parsed.month - 1
  } else {
    const now = parseMonthIso(costEndMonth.value)
    if (now) {
      costPickerYear.value = now.year
      costPickerMonth0.value = now.month - 1
    }
  }
  setTimeout(() => {
    suppressMonthPick.value = false
  }, 0)
})

function getChildren(parentPath: string): any[] {
  const children = accountProducts.value.filter((p: any) => p.parentPath === parentPath)
  console.log(
    `getChildren("${parentPath}"):`,
    children.length,
    'children found:',
    children.map((c) => c.productId)
  )
  return children
}
async function toggleExpand(product: any) {
  const productPath = String(product?.path || '')
  if (!productPath) return
  const isExpanded = expandedProducts.value.has(productPath)
  if (isExpanded) expandedProducts.value.delete(productPath)
  else {
    expandedProducts.value.add(productPath)
    if (!childrenCache.value[productPath]) {
      const children = getChildren(productPath)
      childrenCache.value[productPath] = children
    }
  }
}
function shouldShowExpandIcon(product: any): boolean {
  if (!product || product?.synthetic) return false
  return product.hasChildren === true
}

const accountProductsForTable = computed(() => {
  const list = (accountProducts.value || []).slice()
  const query = String(productSearchAccount?.value || '')
    .trim()
    .toLowerCase()

  console.log(
    'accountProducts.value raw:',
    accountProducts.value.map((p: any) => ({
      productId: p.productId,
      productFriendlyName: p.productFriendlyName,
      level: p.level,
      parentPath: p.parentPath,
      hasChildren: p.hasChildren,
    }))
  )

  let filtered: any[]
  if (!query) {
    filtered = list.slice()
  } else {
    // When searching, include products that match OR have children that match
    const matchingProducts = new Set<string>()

    // First pass: find all products that match the search
    list.forEach((p: any) => {
      const fn = String(p?.productFriendlyName || '').toLowerCase()
      const pn = String(p?.productName || '').toLowerCase()
      const path = String(p?.path || '').toLowerCase()
      const productId = String(p?.productId || '').toLowerCase()

      if (
        fn.includes(query) ||
        pn.includes(query) ||
        path.includes(query) ||
        productId.includes(query)
      ) {
        matchingProducts.add(p.path)

        // Also add all ancestors
        let currentParentPath = p.parentPath
        while (currentParentPath) {
          matchingProducts.add(currentParentPath)
          const parent = list.find((prod: any) => prod.path === currentParentPath)
          currentParentPath = parent?.parentPath
        }
      }
    })

    // Filter to only matching products and their ancestors
    filtered = list.filter((p: any) => matchingProducts.has(p.path))
  }

  const parents = filtered.filter((p: any) => p.level === 0)
  const result: any[] = []
  for (const parent of parents.sort((a, b) =>
    String(a?.path || '').localeCompare(String(b?.path || ''))
  )) {
    const parentPath = String(parent?.path || '')
    const parentId = String(parent?.productId || parent?.path || '')
    const isExpanded = expandedProducts.value.has(parentPath)
    result.push({ ...parent, isParent: true, expanded: isExpanded })
    if (isExpanded && parent.hasChildren) {
      const children = getChildren(parentId)
      for (const child of children.sort((a, b) =>
        String(a?.path || '').localeCompare(String(b?.path || ''))
      )) {
        const childPath = String(child?.path || '')
        const childId = String(child?.productId || child?.path || '')
        const childIsExpanded = expandedProducts.value.has(childPath)
        result.push({ ...child, isParent: child.hasChildren, expanded: childIsExpanded })
        if (childIsExpanded && child.hasChildren) {
          const grandchildren = getChildren(childId)
          for (const grandchild of grandchildren.sort((a, b) =>
            String(a?.path || '').localeCompare(String(b?.path || ''))
          )) {
            result.push({ ...grandchild, isParent: grandchild.hasChildren, expanded: false })
          }
        }
      }
    }
  }
  console.log(
    'accountProductsForTable result:',
    result.map((p: any) => ({
      productId: p.productId,
      productFriendlyName: p.productFriendlyName,
      level: p.level,
      isParent: p.isParent,
      hasChildren: p.hasChildren,
      expanded: p.expanded,
    }))
  )
  return result
})

watch(
  () => route.hash,
  (h) => {
    if (h === '#products' && !productsLoaded.value) {
      productsLoaded.value = true
      loadAccountProducts()
        .then(() => loadCurrentMonthUsageCosts())
    }
  }
)

const navigateToEditAccount = (account: string) => {
  router.push(`/account-admin/edit/${String(account)}`)
}
const openPanels = ref([0])
const addProductForAccount = (_account: string, juid: string) => {
  router.push(`/account/products/${String(juid)}`)
}
const toggleAccountActiveInline = async () => {
  try {
    const name = String(accountName)
    await toggleAccountStatus(name)
    if (accountData.value) accountData.value.is_active = !accountData.value.is_active
    try {
      store.setAccountsCache([])
    } catch {}
  } catch (e) {
    console.error('Failed to toggle account active state', e)
  }
}
const dialogToggleAccount = ref(false)
const confirmToggleAccountActive = () => {
  dialogToggleAccount.value = true
}
const onConfirmToggleAccountActive = async () => {
  await toggleAccountActiveInline()
  dialogToggleAccount.value = false
}
const redirectToDashboard = (juid: string, dashboardUrl: string) => {
  const cookie = new Cookies()
  const admin = cookie.get('admin')
  if (!user?.token) return
  const baseUrl = dashboardUrl.replace(/\/+$/, '')
  window.open(
    `${baseUrl}/?token=${user?.token}&juid=${juid}&role=${user?.role}&redirectionDo=true`,
    '_blank'
  )
}
const onRowClick = (_event: any, row: any) => {
  const item = row?.item ?? row
  const raw = item?.raw ?? item
  if (!raw?.dashboard) return
  const canOpen = (raw?.isActive ?? true) && (raw?.enFlag ?? true)
  if (canOpen) {
    redirectToDashboard(accountData?.value?.juid, raw.dashboard)
  }
}
const getParentPathFrom = (path: string) => {
  const idx = String(path || '').lastIndexOf('/')
  return idx > 0 ? String(path || '').slice(0, idx) : ''
}
const isParentDisabled = (raw: any) => {
  const p = getParentPathFrom(String(raw?.path || ''))
  if (!p) return false
  const parent = (accountProducts.value || []).find(
    (x: any) => String(x?.path || '') === p || String(x?.productName || '') === p.split('/').pop()
  )
  if (!parent) return false
  const isActive = (parent?.isActive ?? true) && (parent?.enFlag ?? true)
  return !isActive
}
const getDisabledChildTooltip = (raw: any) => {
  if (!isParentDisabled(raw)) return null
  const parentPath = getParentPathFrom(String(raw?.path || ''))
  const parent = (accountProducts.value || []).find(
    (x: any) =>
      String(x?.path || '') === parentPath ||
      String(x?.productName || '') === parentPath?.split('/').pop()
  )
  const parentName = parent?.productFriendlyName || parent?.productName || 'parent'
  return `Enable parent first to access this product`
}
const onProductRowClick = (_e: any, r: any) => {
  if (!['company_admin', 'company_suadmin', 'company_owner'].includes(String(userRole.value)))
    return
  const raw = r?.item?.raw || r?.item || r
  if (raw?.synthetic || raw?.isLoadingRow) return
  if (isParentDisabled(raw)) return
  // Use simpleProductName for flat product IDs, fallback to productName for backward compatibility
  const productId = raw?.simpleProductName || raw?.productName
  if (productId) {
    goToEdit(productId)
  }
}
const rowClassForItem = (item: any) => {
  const raw = item?.raw ?? item
  if (raw?.isLoadingRow) return 'loading-row-style'
  if (raw?.synthetic) return 'synthetic-parent'
  if (isParentDisabled(raw)) return 'no-pointer'
  return ''
}
const isParent = (item: any) => {
  const raw = item?.raw ?? item
  return !raw?.isChild
}
const productSearchAccount = ref('')
const productSearchRef = ref()

// Auto-expand parents when their children match the search
watch(productSearchAccount, (newQuery) => {
  const q = (newQuery || '').trim().toLowerCase()
  if (!q) {
    expandedProducts.value.clear()
    return
  }

  // Auto-expand all ancestors when a product matches
  accountProducts.value.forEach((p) => {
    const matches =
      (p.productFriendlyName || '').toLowerCase().includes(q) ||
      (p.productName || '').toLowerCase().includes(q) ||
      (p.productId || '').toLowerCase().includes(q) ||
      (p.path || '').toLowerCase().includes(q)

    if (matches && p.parentPath) {
      // Expand all ancestors up the tree
      let currentParentPath = p.parentPath
      while (currentParentPath) {
        const parent = accountProducts.value.find(
          (prod) => prod.path === currentParentPath || prod.productId === currentParentPath
        )
        if (parent) {
          expandedProducts.value.add(parent.path)
          currentParentPath = parent.parentPath // Continue up the tree
        } else {
          break
        }
      }
    }
  })
})

watch(loadingProd, (val, oldVal) => {
  if (oldVal && !val) {
    setTimeout(() => {
      try {
        productSearchRef.value?.focus?.()
      } catch {}
    }, 0)
  }
})
</script>

<template>
  <v-container>
    <v-row
      id="products"
      v-if="showProductsSection"
      class="table-box"
      :class="role === 'user' ? 'd-none' : ''"
    >
      <v-col cols="12" id="info" class="subheader-sticky">
        <div
          style="
            display: flex !important;
            justify-content: space-between;
            flex-wrap: wrap;
            align-items: center;
          "
        >
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn
              variant="text"
              size="small"
              prepend-icon="mdi-arrow-left"
              class="text-grey-darken-2"
              @click="router.push(`/account-admin/${String(accountData?.accountName || '')}#info`)"
            >
              Back
            </v-btn>
            <h2 class="text-h5 font-weight-medium" style="margin: 0">Account Products</h2>
          </div>
          <div class="d-flex ga-3 text-copy">
            <v-btn
              data-testid="add-product-btn"
              :disabled="!accountData?.is_active"
              v-if="userRole !== 'company_user'"
              variant="text"
              size="small"
              prepend-icon="mdi-plus-box"
              class="text-grey-darken-2"
              @click="
                accountData?.juid &&
                addProductForAccount(
                  String(accountData?.accountName || ''),
                  String(accountData?.juid)
                )
              "
            >
              Add product
            </v-btn>
          </div>
        </div>
      </v-col>

      <v-col cols="12">
        <v-data-table
          data-testid="products-table"
          :class="[
            'elevation-1',
            'striped-rows',
            !['company_admin', 'company_suadmin', 'company_owner'].includes(String(userRole))
              ? 'no-pointer-all'
              : '',
          ]"
          :items-per-page="50"
          :headers="headers"
          :items="accountProductsForTable"
          :loading="isLoading || loadingProd"
          item-value="productName"
          dense
          style="max-width: 100%; width: 100%; overflow-x: hidden"
          :row-class="rowClassForItem"
          @click:row="onProductRowClick"
        >
          <template #item.productFriendlyName="{ item }">
            <v-tooltip
              :text="getDisabledChildTooltip(item)"
              :disabled="!getDisabledChildTooltip(item)"
              location="top"
            >
              <template v-slot:activator="{ props }">
                <div
                  v-bind="props"
                  class="tree-cell"
                  :class="{ 'loading-row': item?.isLoadingRow }"
                  :style="{
                    paddingLeft: `${(item?.level || 0) * 24}px`,
                    opacity: item?.synthetic ? 0.6 : item?.isLoadingRow ? 0.7 : 1,
                  }"
                >
                  <v-progress-circular
                    v-if="item?.isLoadingRow"
                    indeterminate
                    size="16"
                    width="2"
                    class="tree-loading-spinner"
                  ></v-progress-circular>
                  <v-btn
                    v-else-if="shouldShowExpandIcon(item)"
                    icon
                    size="x-small"
                    variant="text"
                    class="tree-expand-btn"
                    @click.stop="toggleExpand(item)"
                  >
                    <v-icon size="small">
                      {{ item?.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                  </v-btn>
                  <span v-else class="tree-spacer"></span>
                  <span class="tree-label" :class="{ 'text-grey': item?.isLoadingRow }">
                    {{ item?.productFriendlyName || item?.productName }}
                  </span>
                </div>
              </template>
            </v-tooltip>
          </template>
          <template v-if="canSeeProductId" #item.productId="{ item }">
            <code>{{ String(item?.path || item?.productName || '') }}</code>
          </template>
          <template #top>
            <div
              v-if="!$vuetify.display.mdAndDown"
              class="bg-grey-lighten-5 d-flex align-center pb-2"
              style="width: 100%; gap: 8px; padding: 8px 8px 0 8px"
            >
              <v-spacer></v-spacer>
              <v-text-field
                data-testid="product-search-input"
                ref="productSearchRef"
                v-model="productSearchAccount"
                placeholder="Search products…"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="compact"
                variant="solo"
                class="search-in-bar mr-2"
                style="max-width: 380px"
              ></v-text-field>
            </div>
            <div v-else class="bg-grey-lighten-5 pb-2" style="width: 100%; padding: 8px 8px 0 8px">
              <v-text-field
                ref="productSearchRef"
                v-model="productSearchAccount"
                placeholder="Search products…"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="comfortable"
                variant="solo"
                style="width: 100%; max-width: 100%"
              ></v-text-field>
            </div>
          </template>
          <template #item.status="{ item }">
            <v-chip size="x-small" :color="item?.isActive ? 'green' : 'red'" variant="tonal">
              {{ item?.isActive ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
          <template #item.type="{ item }">
            <v-chip size="x-small" :color="isParent(item) ? 'blue' : 'grey'" variant="tonal">
              {{ isParent(item) ? 'Parent' : 'Child' }}
            </v-chip>
          </template>
          <template v-if="canSeeMonthlyCost" #header.currentMonthCost>
            <div class="d-flex flex-column align-end" style="width: 100%; gap: 2px">
              <div class="d-flex align-center justify-end" style="width: 100%; gap: 8px">
                <span>Cost</span>
                <v-btn
                  icon
                  size="medium"
                  variant="text"
                  :disabled="usageCostLoading"
                  aria-label="Select start month"
                  @click="costMonthMenu = true"
                >
                  <v-icon size="small">mdi-calendar</v-icon>
                </v-btn>
              </div>
              <div class="text-caption text-medium-emphasis cursor-pointer" @click="costMonthMenu = true">
                {{ String(costStartMonth || '') }} → {{ String(costEndMonth || '') }}
              </div>
            </div>
          </template>
          <template v-if="canSeeMonthlyCost" #item.currentMonthCost="{ item }">
            <div class="d-flex align-center justify-end" style="width: 100%; gap: 8px">
              <v-progress-circular
                v-if="usageCostLoading"
                indeterminate
                size="16"
                width="2"
                class="tree-loading-spinner"
              />
              <span v-else>
                <v-tooltip
                  v-if="
                    ACCOUNT_SERVICE_USAGE_API_URL &&
                    getMonthlyCostUsdForRow(item) != null &&
                    getMonthlyModuleCostsForRow(item).length
                  "
                  location="top"
                >
                  <template #activator="{ props }">
                    <span v-bind="props">
                      {{ formatUsd(getMonthlyCostUsdForRow(item) as number) }}
                    </span>
                  </template>
                  <div class="text-caption" style="min-width: 180px">
                    <div
                      v-for="r in getMonthlyModuleCostsForRow(item)"
                      :key="r.module"
                      class="d-flex align-center justify-space-between"
                      style="gap: 12px"
                    >
                      <span>{{ formatModuleLabel(r.module) }}</span>
                      <span class="font-weight-medium">{{ formatUsdPrecise(r.costUsd) }}</span>
                    </div>
                  </div>
                </v-tooltip>
                <span v-else-if="getMonthlyCostUsdForRow(item) != null">
                  {{ formatUsd(getMonthlyCostUsdForRow(item) as number) }}
                </span>
                <span v-else class="text-medium-emphasis">{{ unavailableCostLabel }}</span>
              </span>
            </div>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex align-center justify-end" style="width: 100%; gap: 6px">
              <v-btn
                data-testid="open-dashboard-btn"
                v-if="item?.dashboard"
                variant="flat"
                color="primary"
                prepend-icon="mdi-open-in-new"
                :disabled="!item?.isActive"
                @click.stop="
                  accountData?.juid &&
                  item?.dashboard &&
                  redirectToDashboard(String(accountData?.juid), String(item?.dashboard))
                "
              >
                Open Dashboard
              </v-btn>
            </div>
          </template>
          <template #no-data>
            <div class="text-center py-6 text-grey-darken-1">No available products</div>
          </template>
        </v-data-table>

        <v-dialog v-if="canSeeMonthlyCost" v-model="costMonthMenu" max-width="420">
          <v-card>
            <v-card-title class="text-subtitle-1 d-flex align-center justify-space-between">
              <span>Cost range</span>
              <v-btn icon size="x-small" variant="text" aria-label="Close" @click="costMonthMenu = false">
                <v-icon size="small">mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-subtitle class="px-4 pb-2 text-caption text-medium-emphasis">
              {{ String(costStartMonth || '') }} → {{ String(costEndMonth || '') }}
            </v-card-subtitle>
            <v-card-text class="pt-0">
              <v-date-picker
                v-model="costStartMonthDate"
                :max="`${costEndMonth}-01`"
                :year="costPickerYear"
                :month="costPickerMonth0"
                :view-mode="costPickerMode"
                @update:view-mode="onCostPickerViewMode"
                @update:year="onCostPickerYear"
                @update:month="onCostPickerMonthUpdate"
              >
                <template #month="{ month, props }">
                  <v-btn
                    v-bind="props"
                    variant="text"
                    @click="onCostPickerMonthSlotClick(props, month?.value)"
                  >
                    {{ month?.text }}
                  </v-btn>
                </template>
              </v-date-picker>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <v-row v-if="showInfoSection">
      <v-col cols="12" id="info" class="subheader-sticky">
        <div
          style="
            display: flex !important;
            justify-content: space-between;
            flex-wrap: wrap;
            align-items: center;
          "
        >
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn
              variant="text"
              size="small"
              prepend-icon="mdi-arrow-left"
              class="text-grey-darken-2"
              @click="router.push('/account-admin')"
            >
              Back
            </v-btn>
            <div class="text-h5 font-weight-medium" style="margin: 0">Account Information</div>
          </div>
          <div class="action-buttons">
            <v-btn
              data-testid="edit-account-btn"
              :disabled="!accountData?.is_active"
              v-if="userRole !== 'company_user'"
              variant="text"
              size="small"
              prepend-icon="mdi-pencil"
              class="text-grey-darken-2"
              @click="
                accountData?.accountName && navigateToEditAccount(String(accountData?.accountName))
              "
            >
              Edit Account Info
            </v-btn>
            <v-btn
              data-testid="add-users-btn"
              v-if="userRole !== 'company_user'"
              :disabled="!accountData?.is_active"
              variant="text"
              size="small"
              prepend-icon="mdi-account"
              class="text-grey-darken-2"
              @click="
                goCreateUser({ name: accountData?.accountNameFriendly, juid: accountData?.juid })
              "
            >
              Add Users
            </v-btn>

            <v-btn
              data-testid="toggle-account-btn"
              v-if="
                ['company_suadmin', 'company_owner', 'company_admin'].includes(String(userRole))
              "
              variant="text"
              size="small"
              class="text-grey-darken-2"
              :prepend-icon="accountData?.is_active ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off'"
              @click="confirmToggleAccountActive()"
            >
              {{ accountData?.is_active ? 'Deactivate' : 'Activate' }}
            </v-btn>
          </div>
        </div>
      </v-col>

      <v-col cols="12">
        <v-expansion-panels v-model="openPanels" multiple>
          <v-expansion-panel>
            <v-expansion-panel-title class="text-h6 font-weight-medium">
              Account Details
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row dense>
                <v-col cols="12" md="6">
                  <dl class="kv">
                    <div>
                      <dt>Account Name</dt>
                      <dd>{{ dash(accountData?.accountName) }}</dd>
                    </div>
                    <div>
                      <dt>Account Name Friendly</dt>
                      <dd>{{ dash(accountData?.accountNameFriendly) }}</dd>
                    </div>
                    <div>
                      <dt>Website</dt>
                      <dd>
                        <template v-if="asLink(accountData?.mainWebsite)">
                          <a
                            :href="asLink(accountData?.mainWebsite)!"
                            target="_blank"
                            rel="noopener"
                          >
                            {{ accountData?.mainWebsite }}
                          </a>
                          <v-btn
                            icon
                            size="x-small"
                            variant="text"
                            class="copyBtn"
                            @click="copyToClipboard(asLink(accountData?.mainWebsite)!)"
                          >
                            <v-icon size="16">mdi-content-copy</v-icon>
                          </v-btn>
                        </template>
                        <template v-else>{{ dash(accountData?.mainWebsite) }}</template>
                      </dd>
                    </div>
                    <div>
                      <dt>JUID</dt>
                      <dd>
                        {{ dash(accountData?.juid) }}
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          class="copyBtn"
                          @click="copyToClipboard(accountData?.juid)"
                        >
                          <v-icon size="16">mdi-content-copy</v-icon>
                        </v-btn>
                      </dd>
                    </div>
                    <div>
                      <dt>Address</dt>
                      <dd>{{ dash(accountData?.address) }}</dd>
                    </div>
                  </dl>
                </v-col>
                <v-col cols="12" md="6">
                  <dl class="kv">
                    <div>
                      <dt>Account Id (a5Account)</dt>
                      <dd>{{ dash(accountData?.a5Account) }}</dd>
                    </div>
                    <div>
                      <dt>Parent Account</dt>
                      <dd>{{ dash(accountData?.parentAccount) }}</dd>
                    </div>
                    <div>
                      <dt>Account Type</dt>
                      <dd>{{ dash(accountData?.accountType) }}</dd>
                    </div>
                    <div>
                      <dt>Dealer PA</dt>
                      <dd>{{ dash(accountData?.dealerPA) }}</dd>
                    </div>
                  </dl>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title class="text-h6 font-weight-medium">
              Primary Contact
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <dl class="kv">
                <div>
                  <dt>Name</dt>
                  <dd>{{ dash(accountData?.primaryContact?.name) }}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    <template v-if="accountData?.primaryContact?.email">
                      <a :href="`mailto:${accountData?.primaryContact?.email}`">
                        {{ accountData?.primaryContact?.email }}
                      </a>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        class="copyBtn"
                        @click="copyToClipboard(accountData?.primaryContact?.email)"
                      >
                        <v-icon size="16">mdi-content-copy</v-icon>
                      </v-btn>
                    </template>
                    <template v-else>—</template>
                  </dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>
                    <template v-if="telHref(accountData?.primaryContact?.phone)">
                      <a :href="telHref(accountData?.primaryContact?.phone)!">
                        {{ accountData?.primaryContact?.phone }}
                      </a>
                    </template>
                    <template v-else>{{ dash(accountData?.primaryContact?.phone) }}</template>
                  </dd>
                </div>
              </dl>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title class="text-h6 font-weight-medium">
              Billing Info
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <dl class="kv">
                <div>
                  <dt>Name</dt>
                  <dd>{{ dash(accountData?.billing?.name) }}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>
                    <div>{{ dash(accountData?.billing?.address_1) }}</div>
                    <div v-if="accountData?.billing?.address_2">
                      {{ accountData?.billing?.address_2 }}
                    </div>
                  </dd>
                </div>
                <div>
                  <dt>City</dt>
                  <dd>{{ dash(accountData?.billing?.city) }}</dd>
                </div>
                <div>
                  <dt>State</dt>
                  <dd>{{ dash(accountData?.billing?.state) }}</dd>
                </div>
                <div>
                  <dt>Zip</dt>
                  <dd>{{ dash(accountData?.billing?.zip) }}</dd>
                </div>
              </dl>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title class="text-h6 font-weight-medium">
              Regional Information
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <dl class="kv">
                <div>
                  <dt>State Code</dt>
                  <dd>{{ dash(accountData?.stateCode) }}</dd>
                </div>
                <div>
                  <dt>Country Code</dt>
                  <dd>{{ dash(accountData?.countryCode) }}</dd>
                </div>
                <div>
                  <dt>Client ID</dt>
                  <dd>{{ dash(accountData?.clientId) }}</dd>
                </div>
                <div>
                  <dt>Initials</dt>
                  <dd>{{ dash(accountData?.initials) }}</dd>
                </div>
                <div>
                  <dt>Timezone</dt>
                  <dd>{{ dash(accountData?.timezone) }}</dd>
                </div>
              </dl>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-title class="text-h6 font-weight-medium">
              Notes
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="note">{{ dash(accountData?.notes) }}</div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-spacer></v-spacer>
    <br />
    <br />

    <v-spacer></v-spacer>
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>Confirm Action</v-card-title>
        <v-card-text>
          <p class="mb-2">
            Are you sure you want to
            {{ selectedAccount && selectedAccount.enabled ? 'enable' : 'disable' }} this product?
          </p>
          <v-alert
            v-if="
              !selectedAccount?.enabled &&
              selectedAccount?.children &&
              selectedAccount.children.length > 0
            "
            type="info"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            <div class="text-body-2">
              <strong>Note:</strong>
              This will also disable {{ selectedAccount.children.length }} child product(s):
              <ul class="mt-2 ml-4">
                <li
                  v-for="child in selectedAccount.children.slice(0, 5)"
                  :key="child"
                  class="text-caption"
                >
                  {{ child }}
                </li>
                <li v-if="selectedAccount.children.length > 5" class="text-caption">
                  ...and {{ selectedAccount.children.length - 5 }} more
                </li>
              </ul>
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="toggleAccountFunc">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogToggleAccount" max-width="420" data-testid="toggle-dialog">
      <v-card>
        <v-card-title>
          Confirm {{ accountData?.is_active ? 'Deactivation' : 'Activation' }}
        </v-card-title>
        <v-card-text>
          Are you sure you want to {{ accountData?.is_active ? 'deactivate' : 'activate' }} this
          account?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            data-testid="toggle-dialog-cancel"
            color="grey"
            variant="text"
            @click="dialogToggleAccount = false"
          >
            Cancel
          </v-btn>
          <v-btn
            data-testid="toggle-dialog-confirm"
            color="primary"
            @click="onConfirmToggleAccountActive"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="removeDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Remove Product</v-card-title>
        <v-card-text>
          <p class="mb-4">
            Are you sure you want to remove
            <strong>{{ productToRemove?.productFriendlyName }}</strong>
            from this account?
          </p>
          <v-alert
            v-if="productToRemove?.children && productToRemove.children.length > 0"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            <div class="text-body-2">
              <strong>Note:</strong>
              This will also remove {{ productToRemove.children.length }} child product(s):
              <ul class="mt-2 ml-4">
                <li
                  v-for="child in productToRemove.children.slice(0, 5)"
                  :key="child"
                  class="text-caption"
                >
                  {{ child }}
                </li>
                <li v-if="productToRemove.children.length > 5" class="text-caption">
                  ...and {{ productToRemove.children.length - 5 }} more
                </li>
              </ul>
            </div>
          </v-alert>
          <v-alert type="warning" variant="tonal" density="compact">
            This action cannot be undone. All product configuration data for this account will be
            permanently deleted.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            :disabled="removingProduct"
            @click="removeDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="removingProduct"
            @click="removeProductFromAccount"
          >
            Remove Product
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" location="top">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<style scoped>
.table-box {
  margin-bottom: 70px;
}
::deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}
::deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}
::deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}
::deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}
::deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
::deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
}
::deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
}
::deep(.striped-rows .v-data-table__tr) {
  color: inherit;
}
.accountOverviewCard .v-card-title {
  font-size: 1.25rem;
  font-weight: bold;
}
.accountOverviewCard .v-card-text {
  font-size: 1rem;
}
@media (max-width: 600px) {
  .accountOverviewCard {
    margin: 10px;
  }
  .accountOverviewCard .v-card-title {
    font-size: 1.1rem;
  }
  .accountOverviewCard .v-card-text {
    font-size: 0.9rem;
  }
  .action-buttons {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
    flex-basis: 100%;
    margin-top: 6px;
  }
}
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
}
.status-text {
  font-weight: 600;
}
.status-active {
  color: #2e7d32;
}
.status-inactive {
  color: #c62828;
}
::deep(.v-expansion-panel) {
  border: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 8px;
}
::deep(.v-expansion-panel:last-child) {
  margin-bottom: 0;
}
::deep(.v-expansion-panel-title) {
  padding: 16px 24px;
  min-height: 56px;
}
::deep(.v-expansion-panel-text__wrapper) {
  padding: 16px 24px 24px;
}
.image-div {
  width: 60px;
  height: 60px;
  border-radius: 100%;
  overflow: hidden;
}
.image-div > img {
  object-fit: cover;
  width: 100%;
}
::deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}
::deep(.v-data-table .no-pointer) {
  cursor: default !important;
}
::deep(.v-data-table .no-pointer:hover) {
  background-color: inherit !important;
}
.v-data-table {
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
}
.no-pointer-all :deep(tbody tr) {
  cursor: default !important;
}
.no-pointer-all :deep(tbody tr:hover) {
  background-color: inherit !important;
}
::deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
::deep(tr.synthetic-parent) {
  background-color: #fafafa !important;
}
::deep(tr.synthetic-parent td) {
  color: #8a8a8a !important;
}
::deep(tr.synthetic-parent:hover) {
  background-color: #f4f4f4 !important;
}
.infoCard .v-card-title {
  font-size: 1.05rem;
  font-weight: 600;
}
.kv {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.kv > div {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 12px;
  align-items: start;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}
.kv > div:last-child {
  border-bottom: 0;
}
.kv dt {
  color: #6b7280;
  font-weight: 600;
  font-size: 0.92rem;
}
.kv dd {
  margin: 0;
  font-size: 0.95rem;
  word-break: break-word;
}
.copyBtn {
  margin-left: 6px;
  opacity: 0.6;
}
.copyBtn:hover {
  opacity: 1;
}
.note {
  white-space: pre-wrap;
  line-height: 1.4;
}
@media (max-width: 720px) {
  .kv > div {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .kv dt {
    font-size: 0.9rem;
  }
  .kv dd {
    font-size: 0.95rem;
  }
}
.subheader-sticky {
  position: sticky;
  top: 56px;
  z-index: 3;
  background: white;
}
@media (max-width: 960px) {
  .subheader-sticky {
    top: 56px;
  }
  .action-buttons {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
    flex-basis: 100%;
    margin-top: 6px;
  }
}
.tree-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}
.tree-expand-btn {
  flex-shrink: 0;
  margin-left: -4px;
}
.tree-spacer {
  width: 28px;
  flex-shrink: 0;
}
.tree-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tree-loading-spinner {
  flex-shrink: 0;
  margin-left: 2px;
  margin-right: 4px;
}
::deep(tr.loading-row-style) {
  cursor: default !important;
  pointer-events: none;
}
::deep(tr.loading-row-style td) {
  font-style: italic;
  color: #999 !important;
}
::deep(tr.loading-row-style:hover) {
  background-color: inherit !important;
}
</style>
